import { adminRecords } from "./adminRecords";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { SudoButton } from "src/sudokit/components/SudoButton";
import Auth from "../Auth";
import { useToast } from "../components/ToastWrapper";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../sudokit/components/Dialog";
import { Text } from "../sudokit/components/Text";
import { TooltipButton } from "../sudokit/components/TooltipButton";
import VStack from "../sudokit/Layout/VStack";


type StripeLinkedAccountsProps = {
  user: any;
  refreshUser?: () => void | Promise<void>;
};

export default function StripeLinkedAccounts({ user, refreshUser }: StripeLinkedAccountsProps) {
  const history = useHistory();
  const [linkedUsers, setLinkedUsers] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [bulkError, setBulkError] = useState<string | null>(null);
  const [deletingUid, setDeletingUid] = useState<string | null>(null);
  const [targetDeleteUser, setTargetDeleteUser] = useState<any | null>(null);
  const [multipleResolved, setMultipleResolved] = useState<boolean>(false);
  const [resolving, setResolving] = useState<boolean>(false);
  const [editingResolved, setEditingResolved] = useState<boolean>(false);
  const { setToast } = useToast();
  const [projectCounts, setProjectCounts] = useState<Record<string, number>>({});

  const fetchLinkedUsers = useCallback(async () => {
    if (!user?.subscription?.data?.stripeCustomerId) {
      setError("No Stripe customer ID found");
      setLinkedUsers([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await Auth.fetchWithIdToken(`/api/admin/stripe-linked-users/${user.subscription.data.stripeCustomerId}`, { method: "POST" });
      if (res.success) {
        setLinkedUsers(res.users);
        try {
          const entries = await Promise.all(
            (res.users || []).map(async (u: any) => {
              try {
                const snap = await adminRecords.collection("projects").where("userId", "==", u.uid).get();
                return [u.uid, snap.docs.length] as const;
              } catch {
                return [u.uid, 0] as const;
              }
            })
          );
          setProjectCounts((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        } catch {}
      } else {
        setError(res.error || "Failed to fetch linked users");
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.subscription?.data?.stripeCustomerId, user?.uid]);

  useEffect(() => {
    fetchLinkedUsers();
  }, [fetchLinkedUsers]);

  const fetchResolvedFlag = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const doc = await adminRecords.collection("subscriptions").doc(user.uid).get();
      const data = doc.exists ? doc.data() || {} : {};
      setMultipleResolved(!!data.multipleEmailsResolved);
    } catch (e: any) {
      // do not block UI on error
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchResolvedFlag();
  }, [fetchResolvedFlag]);

  const markAsResolved = useCallback(async () => {
    if (!user?.uid) return;
    setResolving(true);
    try {
      await Auth.fetchWithIdToken(`/api/admin/logLinkedAccountsResolution`, { uid: user.uid, resolved: true });
      setMultipleResolved(true);
      setToast("Marked as resolved");
    } catch (e: any) {
      setToast(e?.message || "Failed to mark as resolved");
    } finally {
      setResolving(false);
    }
  }, [user?.uid, setToast]);

  const deleteLinkedUser = async (target: any) => {
    if (!target?.uid) return;
    if (target.uid === user.uid) {
      setBulkError("Cannot delete the current user from this panel.");
      return;
    }
    if (linkedUsers.length <= 1) {
      setBulkError("Cannot delete the last remaining linked account.");
      return;
    }

    setDeletingUid(target.uid);
    setBulkError(null);
    try {
      const res = await Auth.fetchWithIdToken(`/api/admin/deleteUser`, {
        uid: target.uid,
        detachFromStripeBeforeDelete: true,
      });
      if (!res?.success) throw new Error(res?.error || "Failed to delete user");

      try {
        await Auth.fetchWithIdToken(`/api/admin/logLinkedAccountsCleanup`, {
          uid: user.uid,
          deleted: [{ uid: target.uid, email: target.email }],
        });
      } catch (e) {}

      await fetchLinkedUsers();
      if (refreshUser) await refreshUser();
    } catch (e: any) {
      try {
        if (e?.response) {
          const data = await e.response.json();
          setBulkError(data?.message || e.message || "Server error");
        } else {
          setBulkError(e?.message || "Server error");
        }
      } catch {
        setBulkError(e?.message || "Server error");
      }
    } finally {
      setDeletingUid(null);
    }
  };

  const openDeleteDialog = (target: any) => {
    if (!target?.uid) return;
    if (target.uid === user.uid) {
      setBulkError("Cannot delete the current user from this panel.");
      return;
    }
    if (linkedUsers.length <= 1) {
      setBulkError("Cannot delete the last remaining linked account.");
      return;
    }
    setTargetDeleteUser(target);
  };

  const confirmDelete = async () => {
    if (!targetDeleteUser) return;
    await deleteLinkedUser(targetDeleteUser);
    setTargetDeleteUser(null);
  };

  if (!user?.subscription?.data?.stripeCustomerId) return null;
  if (loading) return null;
  if (linkedUsers.length <= 1) return null;

  return (
    <div className="my-5 rounded-lg p-4 border border-app-danger-color bg-red-50">
      <VStack spacing={3}>
        <Text
          as="h4"
          size="lg"
          weight="bold"
          color="danger-color"
        >
          ⚠️ Multiple users linked to this Stripe subscription{" "}
          {!multipleResolved || editingResolved ? <span className="text-app-grey-1 font-normal">(select one to make primary and delete the others)</span> : ""}
        </Text>
        {bulkError ? <Text color="danger-color">{bulkError}</Text> : null}
      </VStack>
      {multipleResolved && !editingResolved ? (
        <div className="flex items-center gap-6 flex-wrap mt-4 p-3 rounded border border-red-300 bg-red-100">
          <Text>This user has acknowledged multiple Sudowrite accounts linked to the same Stripe customer and has chosen to keep things as-is.</Text>
          <TooltipButton
            variant="tertiary"
            onClick={() => setEditingResolved(true)}
            aria-label="Edit resolution state"
            tooltip="Temporarily show options to make changes. No changes saved until you act."
          >
            Edit
          </TooltipButton>
        </div>
      ) : error ? (
        <Text color="danger-color">{error}</Text>
      ) : (
        <>
          <table className="w-full border border-app-danger-color bg-white mt-4">
            <thead>
              <tr className="border-b border-app-danger-color bg-red-200">
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">User ID</th>
                <th className="text-left p-2">Projects</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {linkedUsers.map((u: any) => (
                <tr
                  key={u.uid}
                  className={`border-b border-red-200 ${u.uid === user.uid ? "bg-red-50" : "bg-red-100"}`}
                >
                  <td style={{ padding: "8px" }}>
                    {u.uid === user.uid ? (
                      <Text>{u.email}</Text>
                    ) : (
                      <a
                        href={`/admin/user/${u.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open admin page for ${u.email}`}
                      >
                        {u.email}
                      </a>
                    )}
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Text>{u.displayName}</Text>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Text>{u.uid}</Text>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Text>{projectCounts[u.uid] !== undefined ? projectCounts[u.uid] : "..."}</Text>
                  </td>
                  <td style={{ padding: "8px" }}>
                    {u.uid !== user.uid ? (
                      <TooltipButton
                        variant="danger"
                        size="small"
                        state={deletingUid === u.uid ? "loading" : "default"}
                        onClick={() => openDeleteDialog(u)}
                        aria-label={`Delete ${u.email}`}
                        tooltip="Delete this account. It will be unlinked from Stripe and permanently removed."
                      >
                        Delete
                      </TooltipButton>
                    ) : (
                      <Text className="text-xs text-app-grey-1">Current user</Text>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{ marginTop: 10 }}
            className="flex items-center gap-6"
          >
            {!multipleResolved ? (
              <TooltipButton
                variant="secondary"
                disabled={resolving}
                state={resolving ? "loading" : "default"}
                onClick={markAsResolved}
                aria-label="Mark this as resolved and keep accounts as-is"
                tooltip="Hide this section. The user has chosen to keep multiple accounts as-is."
              >
                Mark as Resolved
              </TooltipButton>
            ) : (
              <TooltipButton
                variant="tertiaryGrey"
                onClick={() => setEditingResolved(false)}
                aria-label="Close editing without saving"
                tooltip="Close edit mode without saving changes"
              >
                Close
              </TooltipButton>
            )}
          </div>
        </>
      )}

      <Dialog
        open={!!targetDeleteUser}
        onOpenChange={(open) => {
          if (!open) setTargetDeleteUser(null);
        }}
      >
        <DialogContent aria-label="Confirm deleting linked user">
          <DialogHeader>
            <DialogTitle>Delete linked account</DialogTitle>
            <DialogDescription>
              {targetDeleteUser ? (
                <span>
                  <strong>This will unlink {targetDeleteUser.email}from the Stripe customer and permanently delete their Sudowrite account.</strong> This action cannot be undone. Please make sure if
                  you want to delete this account and that all credits have been transfered over and that you have made a backup of the user's data if necessary.
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <SudoButton
              variant="tertiaryGrey"
              onClick={() => setTargetDeleteUser(null)}
              aria-label="Cancel delete linked user"
            >
              Cancel
            </SudoButton>
            <SudoButton
              variant="danger"
              onClick={confirmDelete}
              state={deletingUid ? "loading" : "default"}
              aria-label="Confirm delete linked user"
            >
              Confirm
            </SudoButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
